import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search, Filter, X, RefreshCw } from "lucide-react";
import { api, pct, thb, TIER_LABELS, type Tier } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrandHeading } from "@/components/BrandFrame";
import { SyncStatus } from "@/components/SyncStatus";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 100;

export default function BrowsePage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [brand, setBrand] = useState<string | "">("");
  const [productMain, setProductMain] = useState<string | "">("");
  const [productLine, setProductLine] = useState<string | "">("");
  const [tierPick, setTierPick] = useState<Tier | "all">("all");
  const [inStock, setInStock] = useState(false);
  // Per-item on-demand stock refresh: an override map keyed by partnumber, the set of in-flight
  // refreshes, and the timestamp of the most recent refresh (shown in the header).
  const [freshInv, setFreshInv] = useState<Record<string, { inv: Inv | null; at: number; source?: string }>>({});
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());
  const [lastRefresh, setLastRefresh] = useState<number | null>(null);

  async function refreshItem(pn: string) {
    setRefreshing((s) => new Set(s).add(pn));
    try {
      const r = await api.productStock(pn, true); // live=1 → real-time pull from BC (D1 fallback)
      setFreshInv((p) => ({ ...p, [pn]: { inv: r.bc_inv as Inv | null, at: r.at, source: r.source } }));
      setLastRefresh(r.at);
    } catch {
      /* surfaced by the spinner stopping; row keeps its prior values */
    } finally {
      setRefreshing((s) => { const n = new Set(s); n.delete(pn); return n; });
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 220);
    return () => clearTimeout(t);
  }, [q]);

  const filtersQ = useQuery({
    queryKey: ["filters", productMain],
    queryFn: () => api.filters(productMain || undefined),
  });

  const visibleTiers: Tier[] = (user?.tiers ?? []) as Tier[];
  const tier = tierPick === "all" ? undefined : tierPick;

  const inf = useInfiniteQuery({
    queryKey: ["products", debouncedQ, brand, productMain, productLine, tier, inStock],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api.products({ q: debouncedQ, brand, productMain, productLine, tier, inStock: inStock ? "1" : undefined, page: pageParam, pageSize: PAGE_SIZE }),
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
  });

  const rows = useMemo(() => inf.data?.pages.flatMap((p) => p.rows) ?? [], [inf.data]);
  const total = inf.data?.pages[0]?.total ?? 0;
  const tiersForRow: Tier[] = tier ? [tier] : visibleTiers;

  const parentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const rowVirt = useVirtualizer({
    count: rows.length + (inf.hasNextPage ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isMobile ? 148 : 72),
    overscan: 8,
  });

  useEffect(() => {
    const last = rowVirt.getVirtualItems().at(-1);
    if (!last) return;
    if (last.index >= rows.length - 5 && inf.hasNextPage && !inf.isFetchingNextPage) {
      inf.fetchNextPage();
    }
  }, [rowVirt.getVirtualItems(), rows.length, inf.hasNextPage, inf.isFetchingNextPage]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <BrandHeading>Master Sale Price 2026</BrandHeading>
          <p className="mt-1 text-sm text-muted-foreground">
            {total.toLocaleString()} รายการ · ระดับราคาของคุณ: {visibleTiers.map((t) => TIER_LABELS[t]).join(" · ")}
            {lastRefresh != null && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700" title="เวลาที่คุณกด 🔄 ดึงสต๊อกสดรายชิ้นล่าสุด">
                <RefreshCw className="h-3 w-3" /> ดึงสต๊อกสดล่าสุด {fmtClock(lastRefresh)}
              </span>
            )}
          </p>
          <div className="mt-2">
            <SyncStatus />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {visibleTiers.length > 1 && (
            <div className="flex rounded-full border border-border bg-muted p-0.5 text-xs">
              <TierToggle active={tierPick === "all"} onClick={() => setTierPick("all")}>ทุกระดับ</TierToggle>
              {visibleTiers.map((t) => (
                <TierToggle key={t} active={tierPick === t} onClick={() => setTierPick(t)}>{TIER_LABELS[t]}</TierToggle>
              ))}
            </div>
          )}
          <button
            onClick={() => setInStock((v) => !v)}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors duration-300",
              inStock
                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700"
                : "border-border bg-background text-muted-foreground hover:border-emerald-500/40 hover:text-emerald-700",
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", inStock ? "bg-emerald-500" : "bg-muted-foreground/40")} />
            มีสต๊อก
          </button>
        </div>
      </div>

      <div className="sticky top-16 z-20 -mx-4 border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="ค้นหารหัสสินค้า, ชื่อรายการ, BC365 code…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-11 pl-10 text-sm"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-secondary">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Chips
              value={brand}
              onChange={setBrand}
              label="แบรนด์"
              options={filtersQ.data?.brands ?? []}
            />
            <Chips
              value={productMain}
              onChange={(v) => { setProductMain(v); setProductLine(""); }}
              label="กลุ่มสินค้า"
              options={filtersQ.data?.productMains ?? []}
            />
            <Chips
              value={productLine}
              onChange={setProductLine}
              label={productMain ? `ใน ${productMain}` : "หมวดย่อย"}
              options={filtersQ.data?.productLines ?? []}
              disabled={!productMain}
              disabledHint="เลือกกลุ่มสินค้าก่อน"
            />
          </div>
        </div>
      </div>

      {/* 290px, not 260 — the header gained the SyncStatus strip; keeps the page itself from scrolling. */}
      <div ref={parentRef} className="h-[calc(100vh-290px)] overflow-auto rounded-lg border border-border bg-card">
        {!isMobile && (
          <div className="sticky top-0 z-10 grid grid-cols-[180px_minmax(260px,1fr)_120px_180px_140px_120px_var(--tier-cols)] items-center gap-3 border-b border-border bg-muted/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
            style={{ "--tier-cols": tiersForRow.map(() => "minmax(170px, 220px)").join(" ") } as React.CSSProperties}>
            <span>รหัสสินค้า</span>
            <span>ชื่อรายการ</span>
            <span>แบรนด์</span>
            <span>กลุ่ม</span>
            <span>สต๊อก</span>
            <span className="text-right">ราคาตั้ง</span>
            {tiersForRow.map((t) => (
              <span key={t} className="text-right">
                {TIER_LABELS[t]}
              </span>
            ))}
          </div>
        )}

        <div style={{ height: rowVirt.getTotalSize(), width: "100%", position: "relative" }}>
          {rowVirt.getVirtualItems().map((vi) => {
            const row = rows[vi.index];
            if (!row) {
              return (
                <div key={vi.key} className="absolute left-0 top-0 w-full px-4 py-3 text-xs text-muted-foreground" style={{ transform: `translateY(${vi.start}px)`, height: vi.size }}>
                  {inf.isFetchingNextPage ? "กำลังโหลดเพิ่ม…" : "—"}
                </div>
              );
            }
            return (
              <div
                key={vi.key}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${vi.start}px)`, height: vi.size }}
              >
                {isMobile
                  ? <MobileRow row={row} tiers={tiersForRow} invOverride={freshInv[row.partnumber]} onRefresh={() => refreshItem(row.partnumber)} refreshing={refreshing.has(row.partnumber)} />
                  : <DesktopRow row={row} tiers={tiersForRow} invOverride={freshInv[row.partnumber]} onRefresh={() => refreshItem(row.partnumber)} refreshing={refreshing.has(row.partnumber)} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

function Chips({ value, onChange, label, options, disabled, disabledHint }: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  options: string[];
  disabled?: boolean;
  disabledHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = options.filter((o) => !search || o.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="relative inline-flex">
      <button
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-semibold transition-colors duration-300 hover:border-beijer/40 hover:text-beijer",
          value && "border-beijer/40 bg-beijer/5 text-beijer",
          disabled && "cursor-not-allowed opacity-50 hover:border-border hover:text-muted-foreground",
        )}
      >
        {label}{value ? `: ${value}` : ""}
      </button>
      {value && !disabled && (
        <button onClick={() => onChange("")} className="absolute -right-1 -top-1 rounded-full bg-destructive p-0.5 text-white">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
      {open && !disabled && (
        <div className="absolute left-0 top-9 z-30 w-72 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="border-b border-border p-1.5">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`กรอง ${options.length} รายการ…`}
              className="w-full rounded bg-secondary px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="max-h-72 overflow-auto p-1">
            <button onClick={() => { onChange(""); setOpen(false); setSearch(""); }} className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-secondary">— ทั้งหมด —</button>
            {filtered.map((o) => (
              <button key={o} onClick={() => { onChange(o); setOpen(false); setSearch(""); }} className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-secondary">{o}</button>
            ))}
            {filtered.length === 0 && <div className="px-2 py-3 text-center text-xs text-muted-foreground">ไม่พบข้อมูล</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function TierToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full px-3 py-1.5 transition-colors duration-300",
        active ? "bg-beijer text-white shadow-sm" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function DesktopRow({ row, tiers, invOverride, onRefresh, refreshing }: { row: any; tiers: Tier[]; invOverride?: { inv: Inv | null; at: number; source?: string }; onRefresh?: () => void; refreshing?: boolean }) {
  const inv = invOverride ? invOverride.inv : (row.bc_inv as Inv | null);
  return (
    <div
      className="grid grid-cols-[180px_minmax(260px,1fr)_120px_180px_140px_120px_var(--tier-cols)] items-start gap-3 border-b border-border/50 px-4 py-2 text-sm transition-colors duration-300 hover:bg-secondary/40"
      style={{ "--tier-cols": tiers.map(() => "minmax(170px, 220px)").join(" ") } as React.CSSProperties}
    >
      <div className="flex flex-col leading-tight pt-0.5 min-w-0">
        <span className="font-mono text-xs font-semibold text-beijer-dark truncate">{row.partnumber}</span>
        {row.bc365_code && <span className="font-mono text-[10px] text-muted-foreground">{row.bc365_code}</span>}
      </div>
      <span className="truncate text-foreground pt-0.5" title={row.description ?? ""}>{row.description ?? "—"}</span>
      <span className="truncate text-xs text-muted-foreground pt-0.5" title={row.bc_brand ?? row.brand2 ?? row.brand1 ?? ""}>{row.bc_brand ?? row.brand2 ?? row.brand1 ?? "—"}</span>
      <div
        className="flex flex-col leading-tight pt-0.5 min-w-0 text-xs"
        title={`${row.bc_product_main_name ?? row.bc_product_main ?? ""}${row.bc_product_line_name ?? row.bc_product_line ? " · " + (row.bc_product_line_name ?? row.bc_product_line) : ""}${(row.bc_product_main || row.bc_product_line) ? ` (${[row.bc_product_main, row.bc_product_line].filter(Boolean).join(" · ")})` : ""}`}
      >
        <span className="text-foreground">{row.bc_product_main_name ?? row.bc_product_main ?? "—"}</span>
        {(row.bc_product_line_name ?? row.bc_product_line) && (
          <span className="text-[10px] text-muted-foreground">{row.bc_product_line_name ?? row.bc_product_line}</span>
        )}
      </div>
      <div className="min-w-0">
        <StockCell inv={inv} fallback={row.stock ?? 0} onRefresh={onRefresh} refreshing={refreshing} freshAt={invOverride?.at} freshLive={invOverride?.source === "live-bc"} />
      </div>
      <span className="text-right font-mono text-xs text-muted-foreground pt-0.5">{thb(row.sale_price)}</span>
      {tiers.map((t) => (
        <TierCell key={t} row={row} tier={t} />
      ))}
    </div>
  );
}

function MobileRow({ row, tiers, invOverride, onRefresh, refreshing }: { row: any; tiers: Tier[]; invOverride?: { inv: Inv | null; at: number; source?: string }; onRefresh?: () => void; refreshing?: boolean }) {
  const inv = invOverride ? invOverride.inv : (row.bc_inv as Inv | null);
  return (
    <div className="border-b border-border/50 px-4 py-3 text-sm transition-colors duration-300">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-xs font-semibold text-beijer-dark">
            {row.partnumber}
            {row.bc365_code && <span className="ml-2 font-normal text-muted-foreground">{row.bc365_code}</span>}
          </div>
          <div className="mt-0.5 truncate text-sm">{row.description ?? "—"}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span>{row.bc_brand ?? row.brand2 ?? row.brand1 ?? "—"}</span>
            <span>·</span>
            <span>{[row.bc_product_main_name ?? row.bc_product_main, row.bc_product_line_name ?? row.bc_product_line].filter(Boolean).join(" · ") || "—"}</span>
          </div>
          <div className="mt-1.5">
            <StockCell inv={inv} fallback={row.stock ?? 0} onRefresh={onRefresh} refreshing={refreshing} freshAt={invOverride?.at} freshLive={invOverride?.source === "live-bc"} />
          </div>
        </div>
        <div className="text-right text-[11px] text-muted-foreground">
          ราคาตั้ง
          <div className="font-mono text-xs">{thb(row.sale_price)}</div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-3">
        {tiers.map((t) => (
          <TierBoxMobile key={t} row={row} tier={t} />
        ))}
      </div>
    </div>
  );
}

type Inv = {
  onHand: number; reserved: number; available: number; onPo: number;
  locs: { l: string; q: number; r: number; a: number }[];
  apQty: number; apReserved: number; apNet: number;
};

// Stock display mirrors the bbt-quotation concept: per-location on-hand badges (AP: 45), the
// AP-branch can-sell number — พร้อมขาย (AP) = AP on-hand − AP reservations, green if >0 else red —
// and a reserved (ผู้จอง) badge. This is the number the sales team actually sells against.
function RefreshBtn({ onRefresh, refreshing }: { onRefresh?: () => void; refreshing?: boolean }) {
  if (!onRefresh) return null;
  return (
    <button
      onClick={onRefresh}
      disabled={refreshing}
      title="อัปเดตสต๊อกชิ้นนี้ให้เป็นล่าสุด"
      className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors duration-300 hover:bg-secondary hover:text-beijer disabled:opacity-50"
    >
      <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
    </button>
  );
}

function StockCell({ inv, fallback, onRefresh, refreshing, freshAt, freshLive }: { inv: Inv | null; fallback: number; onRefresh?: () => void; refreshing?: boolean; freshAt?: number; freshLive?: boolean }) {
  if (!inv) {
    return (
      <div className="flex items-center gap-1">
        <StockBadge qty={fallback} />
        <RefreshBtn onRefresh={onRefresh} refreshing={refreshing} />
      </div>
    );
  }
  const apTip = `พร้อมขาย (AP) = สต๊อก AP (${inv.apQty.toLocaleString()}) − จองที่ AP ${inv.apReserved.toLocaleString()} · เฉพาะคลัง AP`;
  return (
    <div className="flex flex-col gap-0.5 text-[9px] leading-tight tabular-nums">
      <div className="flex items-start justify-between gap-1">
        {inv.locs.length > 0 ? (
          <div className="flex flex-wrap gap-0.5">
            {inv.locs.slice(0, 4).map((l) => (
              <span key={l.l} className="inline-flex items-center rounded bg-secondary px-1 py-0.5 font-semibold text-muted-foreground">
                <span className="uppercase tracking-wide">{l.l}</span>:&nbsp;{Math.floor(l.q).toLocaleString()}
              </span>
            ))}
            {inv.locs.length > 4 && <span className="self-center opacity-60">+{inv.locs.length - 4}</span>}
          </div>
        ) : (
          <span className="text-muted-foreground">On-hand {inv.onHand.toLocaleString()}</span>
        )}
        <RefreshBtn onRefresh={onRefresh} refreshing={refreshing} />
      </div>
      <div className={cn("font-semibold", inv.apNet > 0 ? "text-[#15803d]" : "text-[#B91C1C]")} title={apTip}>
        พร้อมขาย (AP): {inv.apNet.toLocaleString()}
      </div>
      {inv.reserved > 0 && (
        <div className="text-[#EF781A]" title="ยอดจองจาก open sales orders">🔒 ผู้จอง {Math.round(inv.reserved).toLocaleString()} ชิ้น</div>
      )}
      {inv.onPo > 0 && <div className="text-[#0069B4]">PO +{inv.onPo.toLocaleString()}</div>}
      {freshAt != null && (
        <div className="text-[8px] text-emerald-600/80">
          ณ {fmtClock(freshAt)}{freshLive ? " · สด BC" : ""}
        </div>
      )}
    </div>
  );
}

function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function StockBadge({ qty }: { qty: number }) {
  const cls =
    qty <= 0
      ? "bg-muted text-muted-foreground"
      : qty < 5
      ? "bg-[#EF781A]/15 text-[#EF781A]"
      : "bg-[#0069B4]/15 text-[#0069B4]";
  return (
    <span className={cn("inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums", cls)}>
      {qty.toLocaleString()}
    </span>
  );
}

function TierCell({ row, tier }: { row: any; tier: Tier }) {
  const isNet = row[`${tier}_is_net`] === 1;
  const disc = row[`${tier}_disc`];
  const up = row[`${tier}_unit_price`];
  if (row.sale_price == null && up == null) {
    return (
      <div className="flex flex-col items-end leading-tight">
        <Badge variant="license">สอบถาม</Badge>
        <span className="text-[10px] text-muted-foreground">ติดต่อฝ่ายขาย</span>
      </div>
    );
  }
  if (up == null && !isNet) return <span className="text-right text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="font-mono text-sm font-semibold text-foreground">{thb(up)}</span>
      <span className="text-[10px] text-muted-foreground">
        {isNet ? <Badge variant="net">NET</Badge> : <span>−{pct(disc)}</span>}
      </span>
    </div>
  );
}

function TierBoxMobile({ row, tier }: { row: any; tier: Tier }) {
  const isNet = row[`${tier}_is_net`] === 1;
  const disc = row[`${tier}_disc`];
  const up = row[`${tier}_unit_price`];
  const isCallRow = row.sale_price == null && up == null;
  return (
    <div className="rounded-md border border-border bg-secondary/30 p-2">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span><Badge variant={tier}>{TIER_LABELS[tier]}</Badge></span>
        {isCallRow ? <Badge variant="license">สอบถาม</Badge> : isNet ? <Badge variant="net">NET</Badge> : <span className="font-mono text-[10px] text-muted-foreground">−{pct(disc)}</span>}
      </div>
      <div className="mt-1 font-mono text-base font-bold text-foreground">{isCallRow ? <span className="text-xs text-muted-foreground">ติดต่อฝ่ายขาย</span> : thb(up)}</div>
    </div>
  );
}
